import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { adminLoginSchema, type AdminLoginInput } from '@satyanand/shared'
import { LockKeyhole } from 'lucide-react'
import { Button } from '@shared-ui/button'
import { Input } from '@shared-ui/input'
import { Label } from '@shared-ui/label'

interface LoginFormProps {
  onSubmit: (data: AdminLoginInput) => void
  loading: boolean
  error: string | null
  defaultValues?: Partial<AdminLoginInput>
}

export function LoginForm({
  onSubmit,
  loading,
  error,
  defaultValues,
}: LoginFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AdminLoginInput>({
    resolver: zodResolver(adminLoginSchema),
    defaultValues: {
      email: defaultValues?.email || '',
      password: defaultValues?.password || '',
      // totp_code: defaultValues?.totp_code || '',
    },
  })

  return (
    <div className="mx-auto w-full max-w-md rounded-3xl border border-white/15 bg-white p-8 shadow-2xl sm:p-10 animate-in fade-in zoom-in duration-500">
      <div className="flex items-center gap-2 text-slate-600">
        <LockKeyhole className="h-5 w-5 text-blue-700" />
        <span className="text-sm font-black uppercase tracking-widest text-[#0F172A]/60">
          Admin Sign In
        </span>
      </div>

      <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-900 uppercase">
        Welcome
      </h2>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6">
        <div className="space-y-1.5 text-left">
          <Label
            htmlFor="email"
            className="text-xs font-black text-slate-400 uppercase tracking-widest pl-1"
          >
            Email Address
          </Label>
          <Input
            id="email"
            type="email"
            autoComplete="username email"
            placeholder="admin@satyanand.com"
            {...register('email')}
            className={
              errors.email
                ? 'border-red-500 bg-red-50/50'
                : 'bg-slate-50/50 border-slate-200'
            }
          />
          {errors.email && (
            <p className="text-xs text-red-500 font-bold uppercase tracking-tighter pl-1">
              {errors.email.message}
            </p>
          )}
        </div>

        <div className="space-y-1.5 text-left">
          <Label
            htmlFor="password"
            className="text-xs font-black text-slate-400 uppercase tracking-widest pl-1"
          >
            Secure Password
          </Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            {...register('password')}
            className={
              errors.password
                ? 'border-red-500 bg-red-50/50'
                : 'bg-slate-50/50 border-slate-200'
            }
          />
          {errors.password && (
            <p className="text-xs text-red-500 font-bold uppercase tracking-tighter pl-1">
              {errors.password.message}
            </p>
          )}
        </div>

        {/*
        <div className="space-y-1.5 text-left">
          <Label htmlFor="totp_code" className="text-xs font-black text-slate-400 uppercase tracking-widest pl-1">
            TOTP Code (2FA)
          </Label>
          <Input
            id="totp_code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            {...register('totp_code')}
            className={errors.totp_code ? 'border-red-500 bg-red-50/50' : 'bg-slate-50/50 border-slate-200'}
          />
          {errors.totp_code && (
            <p className="text-xs text-red-500 font-bold uppercase tracking-tighter pl-1">{errors.totp_code.message}</p>
          )}
        </div>
        */}

        {error && (
          <div className="rounded-xl border-2 border-red-100 bg-red-50/50 px-4 py-3 text-xs text-red-700 font-black uppercase tracking-widest animate-in slide-in-from-top-2">
            {error}
          </div>
        )}

        <Button
          className="h-14 w-full rounded-2xl bg-blue-700 text-xs font-black uppercase tracking-widest text-white transition-all hover:bg-blue-800 shadow-xl shadow-blue-200 disabled:cursor-not-allowed disabled:opacity-60 active:scale-95"
          disabled={loading}
          type="submit"
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>
      </form>
    </div>
  )
}
